// ============================================================================
// CONSUME TRIGGERED SOURCE
// ============================================================================
// [CREATED: 2026-05-17 — Spec C Task 4]
// [PURPOSE]: When a triggeredEffect firing carries `shouldConsume: true`
//   (Arrow of Slaying destroyed after the hit, Mind Sharpener pearl
//   decrements after a save), the firer pipeline calls this util with the
//   firing's `sourceRef` to apply the consume.
//
//   The util is a pure dispatcher that takes the source ref + an object
//   of injected context handles. Item-stack consume needs `removeItem`
//   (from InventoryContext); infusion-charge consume needs `setPlayerStats`
//   (from CharacterContext). Living in a util — not a context method —
//   keeps the helper testable in isolation and avoids cross-context
//   coupling.
//
// [RULE 11]: Dispatch is on data-driven `type` enum, not on class strings.
//   The infusion branch reads `classKey` from the sourceRef payload —
//   never matches against a literal class name.
//
// [SPEC]: docs/superpowers/specs/2026-05-16-triggered-effect-design.md §5
// [PLAN]: docs/superpowers/plans/2026-05-16-triggered-effect.md Task 4

/**
 * Consume a source referenced by a triggeredEffect firing (shouldConsume:true).
 *
 * sourceRef shape:
 *   - { type: 'item', id: <inventoryItemId> }
 *       → decrements the inventory stack by 1 via ctx.removeItem(id, 1)
 *   - { type: 'infusion', id: <boundItemId>, classKey: <className>, infusionName: <name> }
 *       → decrements charges on the matching binding in
 *         playerStats.classActiveSelections[classKey].infuse_item[N].charges
 *         where boundItemId === id and name === infusionName.
 *   - { type: 'pool', poolId: <string>, amount: <number> }
 *       → decrements amount from playerStats.customFeatPools[poolId].current
 *         (floor at 0, no-op when pool is not found or already at 0).
 *         This is the system-agnostic cost seam: a Daggerheart reaction can
 *         carry { type: 'pool', poolId: 'hope', amount: 1 } instead of a
 *         D&D spell slot.
 *         [Gen-6 — 2026-05-30]: implements the Gen-5-B breadcrumb from
 *         EffectBuilderControls.js emptySpendActionMenuOption JSDoc.
 *
 * @param {Object|null} sourceRef
 * @param {Object} ctx  { removeItem?, setPlayerStats? }
 * @returns {void}
 */
export default function consumeTriggeredSource(sourceRef, ctx = {}) {
  if (!sourceRef || typeof sourceRef !== 'object') return;
  const { type } = sourceRef;
  if (!type) return;

  if (type === 'item') {
    const { id } = sourceRef;
    if (!id) return;
    if (typeof ctx.removeItem !== 'function') return;
    ctx.removeItem(id, 1);
    return;
  }

  if (type === 'infusion') {
    const { id, classKey, infusionName } = sourceRef;
    if (!id || !classKey || !infusionName) return;
    if (typeof ctx.setPlayerStats !== 'function') return;

    ctx.setPlayerStats((prev) => {
      const sels = prev?.classActiveSelections;
      if (!sels) return prev;
      const classSels = sels[classKey];
      if (!classSels) return prev;
      const infusions = classSels.infuse_item;
      if (!Array.isArray(infusions)) return prev;

      // Find the binding by boundItemId + infusionName (both must match).
      // Both fields anchor the binding because a class may have the same
      // infusion bound to multiple items, and the same item could host
      // multiple bindings if a future spec allows that.
      const idx = infusions.findIndex(
        (inf) => inf && inf.boundItemId === id && inf.name === infusionName
      );
      if (idx === -1) return prev;

      // Decrement charges (floor at 0).
      const target = infusions[idx];
      const currentCharges = Number(target.charges) || 0;
      if (currentCharges <= 0) return prev;
      const nextInfusions = infusions.slice();
      nextInfusions[idx] = { ...target, charges: currentCharges - 1 };
      return {
        ...prev,
        classActiveSelections: {
          ...sels,
          [classKey]: { ...classSels, infuse_item: nextInfusions },
        },
      };
    });
    return;
  }

  if (type === 'pool') {
    const { poolId, amount } = sourceRef;
    if (!poolId || typeof poolId !== 'string') return;
    const spend = typeof amount === 'number' && amount > 0 ? amount : 1;
    if (typeof ctx.setPlayerStats !== 'function') return;

    // [Gen-6 — 2026-05-30]: Storage path mirrors ClassResourcesPanel.updateResource
    // classIndex === -2 branch: playerStats.customFeatPools[poolId] = { current, max }.
    // This is the canonical write path for all custom feat pools and is the
    // system-agnostic slot that non-D&D rulesets (e.g., Daggerheart Hope/Fear)
    // can wire a resource pool into without touching D&D-specific class slot machinery.
    ctx.setPlayerStats((prev) => {
      const pools = prev?.customFeatPools;
      if (!pools || typeof pools !== 'object') return prev;
      const pool = pools[poolId];
      if (!pool || typeof pool !== 'object') return prev;
      const current = typeof pool.current === 'number' ? pool.current : (pool.max ?? 0);
      if (current <= 0) return prev;
      const nextCurrent = Math.max(0, current - spend);
      return {
        ...prev,
        customFeatPools: {
          ...pools,
          [poolId]: { ...pool, current: nextCurrent },
        },
      };
    });
    return;
  }

  if (type === 'spellSlot') {
    // [Sub-plan 1H-G-bw Stone 3 / Gen-6 — 2026-05-30]: Consume one spell slot
    // at a given level. Decrements playerStats.spellSlots[level].current by 1
    // (floor 0).
    //
    // [BREADCRUMB — Gen-6 Daggerheart generalization]: Daggerheart does not use
    // spell slots. When the Rule System Editor ships, this branch is generalized
    // to `type:'pool'` with the Daggerheart pool (e.g., hope) as the target pool
    // identifier, via the system-agnostic `{ type:'pool', poolId:'hope', amount:1 }`
    // shape already supported above. Track: Gen-6 row in master-queue.md.
    const { level } = sourceRef;
    const slotLevel = typeof level === 'number' ? level : parseInt(level, 10);
    if (!slotLevel || isNaN(slotLevel) || slotLevel < 1) return;
    if (typeof ctx.setPlayerStats !== 'function') return;

    ctx.setPlayerStats((prev) => {
      const slots = prev?.spellSlots;
      if (!slots || typeof slots !== 'object') return prev;
      const slot = slots[slotLevel];
      if (!slot || typeof slot !== 'object') return prev;
      const current = typeof slot.current === 'number' ? slot.current : 0;
      if (current <= 0) return prev;
      return {
        ...prev,
        spellSlots: {
          ...slots,
          [slotLevel]: { ...slot, current: current - 1 },
        },
      };
    });
    return;
  }

  // Unknown type — no-op (forward-compat: future sourceRef types like
  // 'aura' would extend this).
}
